import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { Component, OnDestroy, OnInit, ViewChild, inject, input } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Params, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { TypeaheadMatch } from 'ngx-bootstrap/typeahead';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { debounceTime, take } from 'rxjs/operators';
import { Uuid } from '../../../../../../graphql';
import { CustomEditTracker } from '../../../../../core/models/custom-edit-tracker';
import { BxLogger } from '../../../../../core/services/bx-logger.service';
import { BxUnverifiedAlertService } from '../../../../../core/services/bx-unverified-alert.service';
import { CataloguesService } from '../../../../../core/services/catalogues.service';
import { DialogParams, DialogService } from '../../../../../core/services/dialog.service';
import { EditTrackerService } from '../../../../../core/services/edit-tracker.service';
import { EstimateEventsService } from '../../../../../core/services/events/estimate-events.service';
import { AsideModalSize, HelperService } from '../../../../../core/services/helper.service';
import { PermissionService } from '../../../../../core/services/permission';
import { FeatureService } from '../../../../../core/services/permission/feature.service';
import { JobItemFactory } from '../../../../../data/factories/job.item.factory';
import { Round } from '../../../../../data/logic/math.functions';
import { TaxService } from '../../../../../data/logic/tax.service';
import {
    EstimateItem,
    ExportFormat,
    Job,
    JobItem,
    JobVariation,
    PurchaseOrder,
    PurchaseOrderCreationSource,
    QuoteRequest,
    ReferenceObjectType,
} from '../../../../../data/models';
import { RepoClientsService } from '../../../../../data/repositories/repo-clients.service';
import { RepoCommonService } from '../../../../../data/repositories/repo-common.service';
import { RepoExportService } from '../../../../../data/repositories/repo-export.service';
import { RepoJobsService } from '../../../../../data/repositories/repo-jobs.service';
import { RepoQuoteRequestsService } from '../../../../../data/repositories/repo-quoterequests.service';
import { BreezeEntity } from '../../../../../data/services/breeze/breeze.service';
import { SharedDataService } from '../../../../../data/services/shared-data.service';
import { ReportConfig } from '../../../../../reports/domain';
import { ReportService } from '../../../../../reports/report.service';
import { SupplierOrderingService } from '../../../../../shared/supplier-ordering/domain/api/supplier-ordering.service';
import { focusElement } from '../../../../../utils/helper-functions/element.functions';
import { BreezeCase } from '../../../../../utils/types/convert-case';
import { LayoutService } from '../../../../layout/layout.service';
import { MessagesService } from '../../../../messages/messages.service';
import { QuoteRequestRecipientApiService } from '../../../../quoterequests/api/quote-request-recipient-api.service';
import { QuoteRequestsService } from '../../../../quoterequests/quoterequest.service';
import { PurchaseOrdersService } from '../../../purchase-orders/purchase-orders.service';
import { JobVariationsService } from '../../variations/job-variations.service';
import { JobActualAddToExistingComponent } from './job-actual-add-to-existing/job-actual-add-to-existing.component';
import { JobActualItemsDetailComponent } from './job-actual-items-detail.component';

@UntilDestroy()
@Component({
    selector: 'app-job-actual-items',
    templateUrl: './job-actual-items.component.html',
    styleUrls: [
        '../../../../../../../src/app/shared/composable-styles/input.scss',
        '../../../../../../../src/app/shared/composable-styles/button.scss',
        './job-actual-items.component.scss',
    ],
    standalone: false,
})
export class JobActualItemsComponent implements OnInit, OnDestroy, CustomEditTracker {
    #supplierOrderingService = inject(SupplierOrderingService);
    #translateService = inject(TranslateService);
    #jobItemFactory = inject(JobItemFactory);
    #helperService = inject(HelperService);
    #reportService = inject(ReportService);
    #bxUnverifiedAlertService = inject(BxUnverifiedAlertService);
    #messagesService = inject(MessagesService);
    #repoExport = inject(RepoExportService);
    #featureService = inject(FeatureService);
    #quoteRequestsService = inject(QuoteRequestsService);
    #purchaseOrdersService = inject(PurchaseOrdersService);
    #editTracker = inject(EditTrackerService);
    #sharedData = inject(SharedDataService);
    #repoJobs = inject(RepoJobsService);
    #jobVariationsService = inject(JobVariationsService);
    #repoQuoteRequests = inject(RepoQuoteRequestsService);
    #repoCustomers = inject(RepoClientsService);
    #logger = inject(BxLogger);
    #repoCommon = inject(RepoCommonService);
    #cataloguesService = inject(CataloguesService);
    #dialogService = inject(DialogService);
    #estimateEventsService = inject(EstimateEventsService);
    #router = inject(Router);
    #quoteRequestRecipientApiService = inject(QuoteRequestRecipientApiService);

    permissionService = inject(PermissionService);
    layoutService = inject(LayoutService);
    taxService = inject(TaxService);

    job = input.required<
        Job & {
            VariancePercent: number;
            VarianceTotal: number;
        }
    >();

    @ViewChild('itemsForm') itemsForm?: NgForm;
    editModes = {
        items: false,
    };
    selectedItems: JobItem[] = [];
    selectedCategory?: JobItem;
    isDeleteCategoryEnabled = false;
    taxCode = '';
    translated: any = {};
    jobItems: JobItem[] = [];
    estimatedTotal_Estimate = 0;
    estimatedTotal_Variations = 0;
    estimatedTotal_Other = 0;
    actualTotal_PurchaseOrders = 0;
    actualTotal_WorkOrders = 0;
    actualTotal_Other = 0;
    showSummaryDetails = false;
    rfqJobs = false;
    quoteRequestSelected = false;
    unitMeasures: string[] = [];
    allSelected = false;
    hasCustomEditTrackerSave: boolean = true;
    saveError: boolean = false;

    quoteRequests: ActualCostingsQuoteRequestSlim[] = [];
    addToQuoteRequestMode = false;
    destinationQuoteRequest?: ActualCostingsQuoteRequestSlim;
    destinationQuoteRequestSelected = false;

    createActions = [
        {
            value: 0,
            label: 'Create New Category',
            condition: this.permissionService.isAvailable('jobs.maintain-job-costings.add-category'),
        },
        {
            value: 1,
            label: 'Create New Empty Quote Request',
            condition: this.permissionService.isAvailable('request-for-quote.maintain-rfqs.create-edit-send-rfq'),
        },
    ];

    canCreateOrder = false;
    hasExistingOrder = false;
    canCreateVariation = false;
    hasExistingVariation = false;
    hasCompactJobs = false;

    filterInput$ = new BehaviorSubject<string>('');
    filterText$ = new BehaviorSubject<string>('');

    ngOnInit(): void {
        this.hasCompactJobs = this.#featureService.isAvailable('compact-jobs');
        this.#featureService
            .getState('quoterequests')
            .pipe(untilDestroyed(this))
            .subscribe((result) => (this.rfqJobs = result && !this.hasCompactJobs));
        this.isDeleteCategoryEnabled = this.permissionService.isAvailable('jobs.maintain-job-costings.delete-category');
        this.taxCode = this.job().CalculationMethod === 0 ? this.#sharedData.UserData.tenant.TaxCode : this.job().TaxRatesShortCode;
        this.translated.PCPS = this.#translateService.instant('general.pc-ps');
        this.translated.P = this.#translateService.instant('estimates.costings.btns.p');
        this.translated.PC = this.#translateService.instant('estimates.costings.btns.pc');
        this.translated.PS = this.#translateService.instant('estimates.costings.btns.ps');
        this.#repoCommon.GetUnitMeasures().then((results) => {
            this.unitMeasures = results.map((i) => i.Description).sort();
        });
        this.canCreateOrder =
            this.permissionService.isAvailable('jobs.maintain-purchase-orders.create-purchase-order') &&
            this.permissionService.isAvailable('jobs.maintain-work-orders.create-work-order');

        this.#estimateEventsService.estimateStatusChanged$.pipe(untilDestroyed(this)).subscribe((e) => {
            if (e.id === this.job().ID_Estimate) {
                this.#determineIfVariationsCanBeCreated();
            }
        });

        this.load();

        this.hasExistingOrder = !!this.job().PurchaseOrders?.some((po) => po.OrderStatus === 'Unsent');
        this.hasExistingVariation = !!this.job().JobVariations?.some((v) => v.Status === 'Pending');

        this.#determineIfVariationsCanBeCreated();

        this.filterInput$.pipe(debounceTime(300)).subscribe((text) => {
            this.filterText$.next(text);
        });

        this.filterText$.subscribe((text) => {
            let firstMatchOpened = false;
            const searchTerm = text.trim().toLowerCase();
            this.jobItems.map((item) => {
                item.children?.map((child) => {
                    child.MatchesSearchText =
                        searchTerm.length == 0 ||
                        !!child.Description?.toLowerCase().includes(searchTerm) ||
                        !!child?.Notes?.toLowerCase().includes(searchTerm) ||
                        !!child?.variation?.Details?.toLowerCase().includes(searchTerm);
                });

                item.MatchesSearchText =
                    searchTerm.length == 0 ||
                    !!item.Description?.toLowerCase().includes(searchTerm) ||
                    (item.children?.some((child) => child.MatchesSearchText) ?? false);

                if (item.MatchesSearchText && !firstMatchOpened) {
                    this.setSelectedCategory(true, item);
                    firstMatchOpened = true;
                }
            });
        });
    }

    ngOnDestroy(): void {
        this.#supplierOrderingService.reset();
        this.filterText$.unsubscribe();
    }

    getHighLightMatchedText(description: string) {
        if (!this.filterText$.value) {
            return description;
        }

        const index = description.toLowerCase().indexOf(this.filterText$.value.toLowerCase());
        if (index < 0) {
            return description;
        }

        return `${description.substring(0, index)}<strong class="actual-costing-search-highlight">${description.substring(
            index,
            index + this.filterText$.value.length
        )}</strong>${description.substring(index + this.filterText$.value.length)}`;
    }

    #determineIfVariationsCanBeCreated() {
        this.canCreateVariation =
            !this.#featureService.isAvailable('compact-jobs') &&
            this.permissionService.isAvailable('jobs.maintain-variations.create-variations') &&
            !(this.job().Estimate && !this.job().Estimate.IsAccepted && !this.job().Estimate.ReadOnly);
    }

    getItemCodeLabel(itemCodeKey: string) {
        return this.translated[itemCodeKey];
    }

    taxRatesChanged(val: string, item: JobItem) {
        item.TaxRates = val;
    }

    load() {
        this.#jobItemFactory.load(this.job(), this.job().JobItems);
        this.jobItems = this.#jobItemFactory.items;
        this.loadItemOrdersAndVariations();
        this.attachEstimateItemsToJobItems();
        //_loadScheduleCosts();
        // Ensure totals are up to date
        this.job().calculateTotals();
        // Changes may have been made on load, such as reordering or recalculation

        this.#repoJobs.Save(null, undefined);
        this.getQuoteRequestForJob();
        this.setSelectedItems();
        this.setSelectedRFQForItems();
        this.#supplierOrderingService.addItemsToOrder(
            this.jobItems.filter((i) => !i.SupplierOrderQuoteId),
            ReferenceObjectType.Job,
            this.job().Estimate?.LivePricingCheckedAt,
            true,
            true
        );
    }

    startEditing(form: NgForm) {
        if (this.permissionService.isAvailable('jobs.maintain-job-costings.modify-actual')) {
            this.editModes.items = true;
            this.#editTracker.start(form, undefined, this);
        }
    }

    async stopEditing(form: NgForm) {
        if (form.valid) {
            await this.save();
            this.editModes.items = false;
            this.#editTracker.stop();
        }
    }

    cancelEditing() {
        this.cancel();
        this.editModes.items = false;
        this.#editTracker.stop();
    }

    dropCategory($event: CdkDragDrop<any>) {
        this.#jobItemFactory.changeCategoryOrder($event.previousIndex, $event.currentIndex);
        if (!this.editModes.items) this.save();
    }

    dropItem($event: CdkDragDrop<any>) {
        // Get currently open parent
        const parent = this.selectedCategory;
        if (!parent) return;
        this.#jobItemFactory.changeItemOrder(parent, $event.previousIndex, $event.currentIndex);
        if (!this.editModes.items) this.save();
    }

    viewOrder(order: PurchaseOrder) {
        return this.#purchaseOrdersService.viewOrder(order).then(() => this.afterSelectedAction());
    }

    viewVariation(variation: JobVariation) {
        return this.#jobVariationsService.viewVariation(variation).then(
            () => {
                this.#jobItemFactory.load(this.job(), this.job().JobItems!);
                this.jobItems = this.#jobItemFactory.items;
                this.loadItemOrdersAndVariations();
            },
            () => ({})
        );
    }

    afterSelectedAction() {
        const currentSelection = this.selectedCategory;
        this.load();
        if (currentSelection) {
            currentSelection.isOpen = false;
            this.setSelectedCategory(true, currentSelection);
        }
        this.clearSelectedItems();
    }

    selectAllChanged() {
        if (this.allSelected) {
            this.filterInput$.next('');
            this.addToQuoteRequestMode = false;
            this.selectAllItems();
        } else {
            this.clearSelectedItems();
        }
    }

    selectAllItems() {
        this.selectedItems = [];
        this.allSelected = true;

        this.jobItems.forEach((item) => {
            item.selected = true;
            item.children?.forEach((childItem) => {
                childItem.selected = true;
                this.selectedItems.push(childItem);
            });
        });

        this.quoteRequestSelected = this.getSelectedItemsQRState();
        if (!this.selectedItems.length) {
            this.addToQuoteRequestMode = false;
        }
    }

    clearSelectedItems() {
        this.jobItems.forEach((item: JobItem) => {
            item.selected = false;
        });
        this.selectedItems.forEach((item: JobItem) => {
            item.selected = false;
        });
        this.selectedItems.length = 0;
        this.quoteRequestSelected = false;
        this.allSelected = false;

        this.addToQuoteRequestMode = false;
        this.destinationQuoteRequest = { Title: '', ID_QuoteRequest: '' };
        this.destinationQuoteRequestSelected = false;
    }

    async orderSelectedItems() {
        const items: any[] = [];
        let contactId: string | undefined = undefined;

        if (this.job().ID_Estimate) {
            const firstSelectedRecipientResult = await firstValueFrom(
                this.#quoteRequestRecipientApiService.getFirstSelectedRecipientForEstimate(this.job().ID_Estimate)
            );
            contactId = firstSelectedRecipientResult.data?.quoteRequestRecipientsV2?.items?.[0]?.iD_Contact;
        }

        this.selectedItems.forEach((item) => {
            const parent = this.jobItems.find((i) => {
                return i.ID_JobItem === item.ParentId;
            });

            let orderedQty = 0;
            let orderedCost = 0;

            if (item.actualOrderItems) {
                item.actualOrderItems.forEach((actualorderitem) => {
                    if (actualorderitem.order.OrderStatus !== 'Cancelled' && actualorderitem.order.IsDeleted === false) {
                        orderedQty += actualorderitem.quantity;
                        orderedCost = Round(orderedCost + actualorderitem.totalCost, 2);
                    }
                });
            }

            let qtyToOrder = item.EstimatedQty - orderedQty;
            let unitCost = item.ActualUnitCost || item.EstimatedUnitCost || Round(item.EstimatedCost / item.EstimatedQty, 2);

            if (item.EstimatedQty === 1 && orderedQty > 0) {
                // See WEB-250, when est qty is 1, assume the balance will be cost based
                qtyToOrder = 1;
                unitCost = Math.max(0, Round(item.EstimatedCost - orderedCost, 2)); // ensure no negatives
            }

            if (item.ID_QuoteRequest && item.ID_QuoteRequestItem) {
                // @ts-ignore TS2532
                const quoteRequest = this.job().QuoteRequests.find((qr) => qr.ID_QuoteRequest === item.ID_QuoteRequest);
                if (quoteRequest && quoteRequest.ID_Job && !quoteRequest.IsDeleted) {
                    // @ts-ignore TS2532
                    const selectedRecipient = quoteRequest.QuoteRequestRecipients.find((qrr) => qrr.IsSelectedQuote);
                    if (selectedRecipient) {
                        const recipientItem = selectedRecipient.QuoteRequestRecipientItems.find(
                            // @ts-ignore TS7006
                            (qrri) => qrri.ID_QuoteRequestItem === item.ID_QuoteRequestItem
                        );
                        if (recipientItem) {
                            const recipientUnitCost = this.#quoteRequestsService.getUnitCostForRFQResponseItem(recipientItem);
                            if (item.EstimatedQty === 1 && recipientItem.Quantity === 1 && orderedQty > 0) {
                                qtyToOrder = 1;
                                unitCost = Math.max(0, Round(recipientUnitCost - orderedCost, 4));
                            } else {
                                qtyToOrder = recipientItem.Quantity - orderedQty;
                                unitCost = item.ActualUnitCost || recipientUnitCost;
                            }
                        }

                        // set contact
                        contactId = selectedRecipient.ID_Contact;
                    }
                }
            }

            // @ts-ignore TS7005
            if (!contactId) contactId = item.getContactId();

            items.push({
                Description: item.Description,
                ID_CostItem: item.ID_CostItem,
                UOM: item.UOM,
                Quantity: qtyToOrder,
                UnitCost: unitCost,
                ID_EstimateItem: item.ID_EstimateItem,
                ID_JobItem: item.ID_JobItem,
                Notes: item.Notes,
                ParentTask: parent ? parent.Description : null,
                ID_ParentTask: parent ? parent.ID_JobItem : null, // BX-507
                ItemCode: item.ItemCode,
                CostItemType: item.CostItemType,
                TaxRates: item.TaxRates,
                TaxContent: item.TaxContent,
            });
        });

        const initData = {} as any;

        initData.ID_Contact = contactId;
        initData.CreationSource = PurchaseOrderCreationSource.SELECTEDJOBITEMS;

        this.#purchaseOrdersService
            .newOrder(this.job(), initData, items)
            .then(() => this.afterSelectedAction())
            .catch(() => ({}));
    }

    variationSelectedItems() {
        // @ts-ignore TS7034
        const items = [];

        this.selectedItems.forEach((item) => {
            const parent = this.jobItems.find((i) => i.ID_JobItem === item.ParentId);

            let variationQty = item.EstimatedQty * -1;
            let unitCost = item.EstimatedUnitCost || Round(item.EstimatedCost / item.EstimatedQty, 2);

            if (item.ActualQty && item.ActualQty !== item.EstimatedQty) {
                variationQty = item.ActualQty - item.EstimatedQty;
                if (!item.EstimatedCost) unitCost = item.ActualUnitCost || Round(item.ActualCost / item.ActualQty, 2);
            } else {
                if (item.ActualCost && item.ActualCost !== item.EstimatedCost) {
                    variationQty = 1;
                    unitCost = item.ActualCost - item.EstimatedCost;
                } else if (item.ActualCost && item.ActualCost === item.EstimatedCost) {
                    variationQty = 0;
                    unitCost = item.EstimatedUnitCost;
                }
            }

            items.push({
                Description: item.Description,
                ID_CostItem: item.ID_CostItem,
                UOM: item.UOM,
                Units: variationQty,
                UnitCost: unitCost,
                ID_EstimateItem: item.ID_EstimateItem,
                ID_JobItem: item.ID_JobItem,
                ParentTask: parent ? parent.Description : null,
                ID_ParentTask: parent ? parent.ID_JobItem : null, // BX-507
                ItemCode: item.ItemCode,
                CostItemType: item.CostItemType,
                TaxRates: item.TaxRates,
                TaxContent: item.TaxContent,
            });

            if (item.ID_QuoteRequest && item.ID_QuoteRequestItem && this.job().QuoteRequests?.length) {
                const quoteRequest = this.job().QuoteRequests!.find((qr) => qr.ID_QuoteRequest === item.ID_QuoteRequest);
                if (quoteRequest && quoteRequest.ID_Job && !quoteRequest.IsDeleted) {
                    if (!quoteRequest.QuoteRequestRecipients?.length) return;
                    const selectedRecipient = quoteRequest.QuoteRequestRecipients.find((qrr) => qrr.IsSelectedQuote);
                    if (selectedRecipient) {
                        const recipientItem = selectedRecipient.QuoteRequestRecipientItems.find(
                            (qrri) => qrri.ID_QuoteRequestItem === item.ID_QuoteRequestItem
                        );
                        if (recipientItem) {
                            items.push({
                                Description: item.Description,
                                ID_CostItem: item.ID_CostItem,
                                UOM: item.UOM,
                                Units: recipientItem.Quantity,
                                UnitCost: this.#quoteRequestsService.getUnitCostForRFQResponseItem(recipientItem),
                                ID_EstimateItem: item.ID_EstimateItem,
                                ID_JobItem: item.ID_JobItem,
                                ParentTask: parent ? parent.Description : null,
                                ID_ParentTask: parent ? parent.ID_JobItem : null, // BX-507
                                ItemCode: item.ItemCode,
                                CostItemType: item.CostItemType,
                                TaxRates: item.TaxRates,
                                TaxContent: item.TaxContent,
                            });
                        }
                    }
                }
            }
        });

        const initData = {} as any;

        this.#jobVariationsService
            // @ts-ignore TS7005
            .newVariation(this.job(), initData, items)
            .then(() => this.afterSelectedAction())
            .catch(() => ({}));
    }

    createNewQuoteRequest() {
        // if in edit mode, update the Job and continue to create the RFQ
        if (this.editModes.items) this.save();

        const calculationMethod = this.job().CalculationMethod;

        this.#quoteRequestsService
            .newQuoteRequest(
                {
                    ID_Estimate: this.job().Estimate?.ID_Estimate,
                    ID_Job: this.job().ID_Job,
                    CalculationMethod: calculationMethod,
                    TaxRates: this.job().TaxRates,
                    items: this.selectedItems.map((e) => {
                        return {
                            Description: e.Description,
                            Quantity: e.EstimatedQty || 0,
                            UOM: e.UOM,
                            ID_JobItem: e.ID_JobItem,
                            ID_CostItem: e.ID_CostItem,
                            ParentID: e.ParentId,
                            CalculationMethod: calculationMethod,
                            TaxRates: this.taxService.convertIncomeRatesToExpense(this.job().TaxRates, e.TaxRates, e.CostItemType),
                            CostItemType: e.CostItemType,
                            AllowQtyEdit: !(e.ID_EstimateItem || (this.job().Estimate?.ID_Estimate && !this.job().IsInvoiceMethodCostPlus)),
                        };
                    }),
                },
                null,
                this.job()
            )
            .then((quoteRequest: any) => {
                this.selectedItems.forEach((jobItem) => {
                    // @ts-ignore TS7006
                    quoteRequest.QuoteRequestItems.forEach((item) => {
                        if (item.ID_JobItem === jobItem.ID_JobItem) {
                            jobItem.ID_QuoteRequest = item.ID_QuoteRequest;
                            jobItem.ID_QuoteRequestItem = item.ID_QuoteRequestItem;
                        }
                    });
                });

                this.save().then(() => {
                    this.afterSelectedAction();
                });
            })
            .catch(() => ({}));
    }

    selectedChanged(item: JobItem) {
        if (item.selected) {
            this.selectedItems.push(item);
        } else {
            const index = this.selectedItems.indexOf(item);
            if (index !== -1) this.selectedItems.splice(index, 1);
        }
        this.quoteRequestSelected = this.getSelectedItemsQRState();

        const selectedItemsForCategoryCount = this.selectedItems.filter((c) => c.ParentId === item.ParentId).length;
        const category = this.jobItems.find((c) => c.ID_JobItem === item.ParentId && c.IsDeleted === false);
        const allItemsInCategoryCount = (category?.children || []).filter((c) => c.IsDeleted === false).length;

        if (category && selectedItemsForCategoryCount === allItemsInCategoryCount) {
            category.selected = true;
        } else if (category && category.selected && selectedItemsForCategoryCount !== allItemsInCategoryCount) {
            category.selected = false;
        }

        this.checkIfAllSelected();
    }

    selectedCategoryChanged(category: JobItem) {
        if (category.selected) {
            category.children?.forEach((child) => {
                if (!child.selected) {
                    child.selected = true;
                    this.selectedItems.push(child);
                }
            });
        } else {
            category.children?.forEach((child) => {
                const index = this.selectedItems.indexOf(child);
                if (index !== -1) this.selectedItems.splice(index, 1);
                child.selected = false;
            });
        }

        this.quoteRequestSelected = this.getSelectedItemsQRState();
        if (!this.selectedItems.length) {
            this.addToQuoteRequestMode = false;
        }
        this.checkIfAllSelected();
    }

    checkIfAllSelected() {
        const count = this.jobItems.reduce((sum, item) => sum + (item.children?.length ?? 0), 0);

        this.allSelected = count > 0 && this.selectedItems.length === count;
    }

    changeCategoryOrder(oldIndex: number, newIndex: number) {
        this.#jobItemFactory.changeCategoryOrder(oldIndex, newIndex);
        if (!this.editModes.items) this.save();
    }

    changeItemOrder(oldIndex: number, newIndex: number) {
        // Get currently open parent
        const parent = this.selectedCategory;
        if (!parent) return;
        this.#jobItemFactory.changeItemOrder(parent, oldIndex, newIndex);
        if (!this.editModes.items) this.save();
    }

    clearItemCode(item: JobItem) {
        if (!this.editModes.items) this.editModes.items = true;
        item.ItemCode = null;
    }

    save(msg?: string) {
        return this.#repoJobs.Save(null, msg || 'Saved Actuals').then(
            () => {
                this.loadItemOrdersAndVariations();
                this.saveError = false;
            },
            () => {
                this.saveError = true;
            }
        );
    }

    deleteCategory(evt: Event, category: JobItem) {
        evt.stopPropagation();

        if (category?.children?.some((c: JobItem) => !c.IsDeleted && c.ID_QuoteRequest !== null)) {
            const params = {
                title: 'Delete Category',
                text: 'The category cannot be deleted until the associated Request for Quotes (RFQs) are deleted.',
                showCancel: false,
                okText: 'Close',
            } satisfies DialogParams;
            this.#dialogService.show(params);
            return;
        }

        const params = {
            title: 'Delete Category and ALL child items?',
            text: `Confirm you want to delete ${category.Description}?`,
        };
        this.#dialogService.show(params).then(
            () => {
                this.#jobItemFactory.deleteCategory(category);
                category.children?.forEach((childItem) => this.removeSelection(childItem));
                this.save('Category Deleted');
            },
            () => ({})
        );
    }

    deleteItem(evt: Event, item: JobItem) {
        evt.stopPropagation();
        if (this.editModes.items) {
            this.#jobItemFactory.deleteItem(item);
            this.removeSelection(item);
        } else {
            const params = {
                title: 'Delete Item?',
                text: `Confirm you want to delete ${item.Description}?`,
            };
            this.#dialogService.show(params).then(
                () => {
                    this.#jobItemFactory.deleteItem(item);
                    this.removeSelection(item);
                    this.save('Item Deleted');
                },
                () => ({})
            );
        }
    }

    addCategory(form: NgForm) {
        const category = this.#jobItemFactory.addCategory();
        this.setSelectedCategory(true, category);
        this.startEditing(form);
        focusElement(category.ID_JobItem);
    }

    setSelectedCategory(state: boolean, category: JobItem) {
        if (!state) return;
        if (this.editModes.items) {
            this.#editTracker.submit();
        }
        this.selectedCategory = category;

        if (this.selectedCategory.isOpen) return;

        this.jobItems.forEach((i) => {
            if (i.isOpen) i.isOpen = false;
        });

        setTimeout(() => {
            //this needs to be pushed back to the next frame rendered so the scrolltotop directive calculates the correct delta y
            if (this.selectedCategory) this.selectedCategory.isOpen = true;
        }, 1);
    }

    addItem(category: JobItem, itemsForm: NgForm) {
        const item = this.#jobItemFactory.addItem(category) as JobItem;
        this.startEditing(itemsForm);
        focusElement(item.ID_JobItem);
        if (category.selected) {
            item.selected = true;
            this.selectedItems.push(item);
        }
    }

    addItemModal(category: JobItem) {
        const item = this.#jobItemFactory.addItem(category);
        this._viewJobItemDetail(item, category);
    }

    cancel() {
        this.cancelItems();
        this.cancelJob();
        this.clearSelectedItems();
    }

    cancelItems() {
        for (let i = this.jobItems.length - 1; i >= 0; i -= 1) {
            const item = this.jobItems[i];

            if (!item) continue;

            // mark for removal from array
            if (item.entityAspect.entityState.isAdded()) this.jobItems.splice(i, 1);

            this.cancelChildren(item);

            item.entityAspect.rejectChanges();
        }
    }

    cancelJob() {
        this.job().entityAspect.rejectChanges();
    }

    cancelChildren(item: JobItem) {
        if (!item.children || item.children.length === 0) return;

        for (let i = item.children.length - 1; i >= 0; i -= 1) {
            const childItem = item.children[i];
            if (!childItem) continue;
            if (childItem.entityAspect.entityState.isAdded()) item.children.splice(i, 1);
            if (childItem.children && childItem.children.length > 0) this.cancelChildren(childItem);

            childItem.entityAspect.rejectChanges();
        }
    }

    loadItemOrdersAndVariations() {
        const jobItemOrderInfo = this.#repoJobs.GetJobItemOrderInfo(
            this.job().JobVariations,
            this.job().JobItems,
            this.job().PurchaseOrders
        );

        this.estimatedTotal_Estimate = jobItemOrderInfo.estimatedTotal;
        this.estimatedTotal_Variations = jobItemOrderInfo.estimatedVariationTotal;
        this.estimatedTotal_Other = jobItemOrderInfo.estimatedOtherTotal;
        this.actualTotal_PurchaseOrders = jobItemOrderInfo.actualPurchaseOrderTotal;
        this.actualTotal_WorkOrders = jobItemOrderInfo.actualWorkOrderTotal;
        this.actualTotal_Other = jobItemOrderInfo.actualOtherTotal;
    }

    attachEstimateItemsToJobItems() {
        if (this.job().Estimate) {
            // Create hash lookup from ID_EstimateItem to item object
            const lookup = new Map();
            this.job().Estimate.EstimateItems.forEach((i) => {
                if (!i.IsDeleted) {
                    lookup.set(i.ID_EstimateItem, i);
                }
            });

            // Attach matching estimate item to job item
            // @ts-ignore TS2532
            this.job().JobItems.forEach((i: any) => {
                if (!i.IsDeleted && i.ID_EstimateItem) {
                    i.EstimateItem = lookup.get(i.ID_EstimateItem);
                }
            });
        }
    }

    jobItemHasChildrenWithReceivedOrders(jobItem: JobItem) {
        return (
            jobItem &&
            jobItem.children &&
            jobItem.children.length > 0 &&
            jobItem.children.filter((childItem) => {
                return childItem && childItem.hasReceivedOrders;
            }).length > 0
        );
    }

    printJobCostingsEstVsAct() {
        const params = {
            reportName: 'jobcostingsactuals',
            reportTitle: 'Job Estimated vs Actual Costs',
            params: {
                ID_Job: this.job().ID_Job,
            },
            fileName: this.job().Number ? this.job().Number + '-JobCostingsActual' : '',
        };
        this.#reportService.generate(params);
    }

    // TODO:: move to shared component to slim the product
    emailJobCostingsEstVsAct() {
        if (!this.#sharedData.UserData.identityInfo.email_verified) {
            this.#bxUnverifiedAlertService.trigger(this.#sharedData.UserData.user);
            return;
        }

        const params: ReportConfig = {
            reportName: 'jobcostingsactuals',
            reportTitle: 'Job Estimated vs Actual Costs',
            params: {
                ID_Job: this.job().ID_Job,
            },
            format: 'pdf',
            fileName: this.job().Number ? this.job().Number + '-JobCostingsActual' : '',
        };
        // @ts-ignore TS2532
        this.#reportService.generate(params).then((response) => {
            return this.#messagesService.sendEmail({
                To: [],
                Subject: this.job().FullDescription + ' - Job Costings Actual items',
                Body: '',
                StorageAttachments: [response.data],
                references: [
                    {
                        Type: 'job',
                        Id: this.job().ID_Job,
                    },
                    {
                        Type: 'estimate',
                        Id: this.job().ID_Estimate,
                    },
                ],
            });
        });
    }

    printJobCostingsEstVsComm() {
        const params = {
            reportName: 'jobcostingscommitted',
            reportTitle: 'Job Estimated vs Committed Costs',
            params: {
                ID_Job: this.job().ID_Job,
            },
            fileName: this.job().Number ? this.job().Number + '-JobCostingsCommitted' : '',
        };
        this.#reportService.generate(params);
    }

    // TODO:: move to shared component to slim the product
    emailJobCostingsEstVsComm() {
        if (!this.#sharedData.UserData.identityInfo.email_verified) {
            this.#bxUnverifiedAlertService.trigger(this.#sharedData.UserData.user);
            return;
        }

        const params: ReportConfig = {
            reportName: 'jobcostingscommitted',
            reportTitle: 'Job Estimated vs Committed Costs',
            params: {
                ID_Job: this.job().ID_Job,
            },
            format: 'pdf',
            fileName: this.job().Number ? this.job().Number + '-JobCostingsCommitted' : '',
        };
        // @ts-ignore TS2532
        this.#reportService.generate(params).then((response) => {
            return this.#messagesService.sendEmail({
                To: [],
                Subject: this.job().FullDescription + ' - Job Costings Committed items',
                Body: '',
                StorageAttachments: [response.data],
                references: [
                    {
                        Type: 'job',
                        Id: this.job().ID_Job,
                    },
                    {
                        Type: 'estimate',
                        Id: this.job().ID_Estimate,
                    },
                ],
            });
        });
    }

    printJobCostingsPCPS() {
        const params: ReportConfig = {
            reportName: 'jobcostingspcps',
            reportTitle: `Job Estimated vs Actual ${this.translated.PCPS} Items`,
            params: {
                ID_Job: this.job().ID_Job,
                PrintPcItems: true,
                PrintPsItems: true,
                CalculationMethod: this.job().CalculationMethod,
            },
            fileName: this.job().Number ? this.job().Number + '-JobCostingsPCPS' : '',
        };
        this.#reportService.generate(params);
    }

    // TODO:: move to shared component to slim the product
    emailJobCostingsPCPS() {
        if (!this.#sharedData.UserData.identityInfo.email_verified) {
            this.#bxUnverifiedAlertService.trigger(this.#sharedData.UserData.user);
            return;
        }

        const params: ReportConfig = {
            reportName: 'jobcostingspcps',
            reportTitle: `Job Estimated vs Actual ${this.translated.PCPS} Items`,
            params: {
                ID_Job: this.job().ID_Job,
                PrintPcItems: true,
                PrintPsItems: true,
                CalculationMethod: this.job().CalculationMethod,
            },
            format: 'pdf',
            fileName: this.job().Number ? this.job().Number + '-JobCostingsPCPS' : '',
        };
        // @ts-ignore TS2532
        this.#reportService.generate(params).then((response) => {
            this._getEmailTo().then((to) => {
                return this.#messagesService.sendEmail({
                    To: to,
                    Subject: `${this.job().FullDescription} - Job Costings for ${this.translated.PCPS} Items`,
                    Body: '',
                    StorageAttachments: [response.data],
                    references: [
                        {
                            Type: 'job',
                            Id: this.job().ID_Job,
                        },
                        {
                            Type: 'estimate',
                            Id: this.job().ID_Estimate,
                        },
                    ],
                });
            });
        });
    }

    _viewJobItemDetail(item: JobItem, parent: JobItem) {
        const initialState = {
            item,
            parent,
        };
        const modalRef = this.#helperService.showModalAside(JobActualItemsDetailComponent, { initialState });
        // @ts-ignore TS2532
        modalRef.content.action.pipe(take(1)).subscribe((result) => {
            if (result.cancelled) {
                this.cancel();
            } else {
                this.save('Saved Item').then(() => {
                    this.load();
                });
            }
        });
    }

    _getEmailTo() {
        return new Promise<string[] | string | null>((resolve, reject) => {
            const defaultTo: { email: string; name: string }[] = [];

            if (this.job().ClientEmail) {
                defaultTo.push({
                    email: this.job().ClientEmail,
                    name: this.job().ClientName,
                });
            }

            if (this.job().ID_Customer) {
                return this.#repoCustomers.Get(this.job().ID_Customer!).then((customer) => {
                    const contacts = this.#repoCustomers.GetEmailSMSContacts(customer);
                    // @ts-ignore TS7005
                    if (!contacts.email.length) resolve(defaultTo);
                    else resolve(contacts.email);
                });
            } else {
                // @ts-ignore TS2345
                return resolve(defaultTo);
            }
        });
    }

    exportItems() {
        this.#repoExport.exportJobItems({
            jobId: this.job().ID_Job,
            jobNumber: this.job().Number,
            format: ExportFormat.XLSX,
        });
    }

    private setSelectedItems() {
        this.selectedItems = [];
        this.jobItems.forEach((category: JobItem) => {
            if (category.children) {
                category.children.forEach((item: JobItem) => {
                    if (item.selected) this.selectedItems.push(item);
                });
            }
        });
        this.quoteRequestSelected = this.getSelectedItemsQRState();
    }

    private getSelectedItemsQRState(): boolean {
        return (
            this.selectedItems.some((ji) => ji.ID_QuoteRequest) ||
            (this.job().ID_Estimate && this.selectedItems.some((item) => !item.ID_EstimateItem) && this.job().InvoiceMethod !== 1)
        );
    }

    private removeSelection(item: JobItem) {
        const index = this.selectedItems.indexOf(item);
        if (index !== -1) this.selectedItems.splice(index, 1);
        this.quoteRequestSelected = this.getSelectedItemsQRState();
    }

    private getQuoteRequestForJob() {
        if (!this.job().QuoteRequests || !this.job().QuoteRequests?.length) return;
        this.job().QuoteRequests!.forEach((quoteReq) => {
            if (
                quoteReq.Status === 'Draft' &&
                !quoteReq.IsStandalone &&
                !this.quoteRequests.find((item) => item.ID_QuoteRequest === quoteReq.ID_QuoteRequest)
            ) {
                this.quoteRequests.push({
                    Title: 'QR ' + quoteReq.RequestNumber + ' - ' + quoteReq.Title + ' (Draft)',
                    ID_QuoteRequest: quoteReq.ID_QuoteRequest,
                });
            }
        });
    }

    setAddToQuoteRequestMode() {
        this.showSummaryDetails = false;
        this.addToQuoteRequestMode = true;
    }

    selectedQuoteTypeAheadItem($model: TypeaheadMatch<ActualCostingsQuoteRequestSlim>) {
        this.destinationQuoteRequestSelected = true;
        this.destinationQuoteRequest = $model.item;
    }

    addItemsToAnotherRFQ() {
        if (!this.destinationQuoteRequest) return;

        Promise.all([
            this.#repoQuoteRequests.GetQuoteRequestGroups(this.destinationQuoteRequest.ID_QuoteRequest),
            this.#repoQuoteRequests.GetQuoteRequestItems(this.destinationQuoteRequest.ID_QuoteRequest),
        ]).then((data) => {
            // Map from estimate item to the group
            const groups = new Map<string, string>();
            data[0].results.forEach((groupItem) => groups.set(groupItem.ID_JobItem, groupItem.ID_QuoteRequestGroup));

            // Map from quote request group to ownering group
            const items = new Map<string, string>();
            data[1].results.forEach((item) => groups.set(item.ID_QuoteRequestItem, item.ID_QuoteRequestGroup));

            const allEntities = [...data[0].results, ...data[1].results];
            let maxOrder = allEntities.reduce((a, b) => Math.max(a, b.Order), 0);

            this.selectedItems.forEach((item) => {
                // If the items group is not already in the quote request, create a quote request group
                let groupId = groups.get(item.ParentId);
                if (!groupId) {
                    const groupItem = this.jobItems.find((e) => e.ID_JobItem === item.ParentId);

                    if (!!groupItem) {
                        const data = {
                            ID_QuoteRequest: this.destinationQuoteRequest!.ID_QuoteRequest,

                            ID_JobItem: groupItem.ID_JobItem,

                            Description: groupItem.Description,
                            Order: ++maxOrder,
                        };

                        const quoteRequestGroup = this.#repoQuoteRequests.CreateGroup(data);
                        allEntities.push(quoteRequestGroup);
                        groupId = quoteRequestGroup.ID_QuoteRequestGroup;
                        if (groupId) {
                            groups.set(groupItem.ID_JobItem, groupId);
                        }
                    }
                }

                item.ID_QuoteRequest = this.destinationQuoteRequest!.ID_QuoteRequest;

                // Find the last Order value for the group
                let lastOrder = allEntities.reduce((a, b) => {
                    let itemGroupId = b.ID_QuoteRequestGroup ?? items.get(b.ID_QuoteRequestItem);
                    return Math.max(a, itemGroupId === groupId ? b.Order : a);
                }, 0);

                // Shift the items after the group by one, to make room for the new item
                allEntities.forEach((e) => (e.Order += e.Order > lastOrder ? 1 : 0));
                maxOrder++;

                const data = $.extend({
                    ID_QuoteRequest: this.destinationQuoteRequest!.ID_QuoteRequest,
                    ID_QuoteRequestGroup: groupId,
                    Order: ++lastOrder,
                    Description: item.Description,
                    Quantity: item.EstimatedQty || 0,
                    UOM: item.UOM,
                    ID_JobItem: item.ID_JobItem,
                    ParentID: item.ParentId,
                    CalculationMethod: this.job().CalculationMethod,
                    TaxRates: this.taxService.convertIncomeRatesToExpense(this.job().TaxRates, item.TaxRates, item.CostItemType),
                    CostItemType: item.CostItemType,
                    AllowQtyEdit: !(item.ID_EstimateItem || (this.job().Estimate?.ID_Estimate && !this.job().IsInvoiceMethodCostPlus)),
                });

                const newItem = this.#repoQuoteRequests.CreateItem(data);
                item.ID_QuoteRequestItem = newItem.ID_QuoteRequestItem;
                // @ts-ignore TS7052
                items.set(newItem.ID_QuoteRequestItem, newItem.ID_QuoteRequestGroup);
            });

            this.clearSelectedItems();

            this.#repoQuoteRequests.Save(null, undefined);
            this.#repoJobs.Save(null, undefined);
            this.#logger.success('Quote Request Updated');
        });
    }

    addItemsToAnotherOrderOrVariation() {
        const ref = this.#helperService.showModalAside(
            JobActualAddToExistingComponent,
            {
                initialState: {
                    jobId: this.job().ID_Job,
                    canCreateOrders: this.canCreateOrder,
                    canCreateVariations: this.canCreateVariation,
                },
            },
            AsideModalSize.medium
        );

        ref.content?.selection.pipe(untilDestroyed(this)).subscribe((result) => {
            if (result.type === 'Order') this.#addItemsToAnotherOrder(result.id);
            else if (result.type === 'Variation') this.#addItemsToAnotherVariation(result.id);
        });
    }

    async #addItemsToAnotherOrder(orderId: Uuid) {
        const order = (await this.#repoJobs.GetJobPurchaseOrder(orderId, this.job().ID_Job)).results[0] as BreezeEntity & PurchaseOrder;

        const parents = new Map<string, string>();
        this.selectedItems.forEach((item) => {
            if (!parents.has(item.ParentId)) {
                const parentItem = this.jobItems.find((e) => e.ID_JobItem === item.ParentId);
                if (!parentItem) return;
                parents.set(parentItem.ID_JobItem, parentItem.Description);
            }
            const parent = parents.get(item.ParentId);
            this.#repoJobs.CreatePurchaseOrderItem(order, {
                ID_JobItem: item.ID_JobItem,
                ID_CostItem: item.ID_CostItem,
                Description: item.Description,
                UnitCost: item.EstimatedUnitCost,
                Quantity: item.EstimatedQty,
                TotalCost: item.EstimatedCost,
                UOM: item.UOM,
                Notes: item.Notes,
                ParentTask: parent,
                ID_ParentTask: item.ParentId,
                ItemCode: item.ItemCode,
            });
        });

        this.viewOrder(order);
    }

    async #addItemsToAnotherVariation(variationId: Uuid) {
        const variation = (await this.#repoJobs.GetJobVariation(variationId, this.job().ID_Job)).results[0] as BreezeEntity & JobVariation;

        const parents = new Map<string, string>();
        this.selectedItems.forEach((item) => {
            if (!parents.has(item.ParentId)) {
                const parentItem = this.jobItems.find((e) => e.ID_JobItem === item.ParentId);
                if (!parentItem) return;
                parents.set(parentItem.ID_JobItem, parentItem.Description);
            }
            const parent = parents.get(item.ParentId);
            this.#repoJobs.CreateJobVariationTask(variation, {
                ID_JobItem: item.ID_JobItem,
                ID_CostItem: item.ID_CostItem,
                Description: item.Description,
                Units: item.EstimatedQty,
                UnitCost: item.EstimatedUnitCost,
                TotalCost: item.EstimatedCost,
                UOM: item.UOM,
                ParentTask: parent,
                ID_ParentTask: item.ParentId,
            });
        });

        this.viewVariation(variation);
    }

    getQuoteRequestNumberForJobItem(jobItem: JobItem) {
        return this.getQuoteRequestNumberForItem(jobItem, this.job().QuoteRequests);
    }

    getQuoteRequestNumberForEstimateItem(estimateItem: EstimateItem) {
        return this.getQuoteRequestNumberForItem(estimateItem, this.job().Estimate.QuoteRequests);
    }

    getQuoteRequestNumberForItem(item: any, collection?: BreezeCase<QuoteRequest>[]) {
        if (!item || !item.ID_QuoteRequest) return null;

        const qr = (collection ?? []).find((qr) => {
            return qr.ID_QuoteRequest === item.ID_QuoteRequest;
        });
        return qr ? 'QR' + qr.RequestNumber + (item.ID_Job ? ' (Job)' : '') : '';
    }

    getQuoteRequestClassForJobItem(jobItem: JobItem) {
        return this.getQuoteRequestClassForItem(jobItem, this.job());
    }

    getQuoteRequestClassForEstimateItem(estimateItem: EstimateItem) {
        return this.getQuoteRequestClassForItem(estimateItem, this.job().Estimate);
    }

    getQuoteRequestClassForItem(item: any, collection: any) {
        if (!item || !item.ID_QuoteRequest) return '';
        const qr = collection.QuoteRequests.find((qr: any) => {
            return qr.ID_QuoteRequest === item.ID_QuoteRequest;
        });
        if (!qr) return '';
        return qr ? 'label-' + this.#sharedData.labelStatus[qr.Status] : '';
    }

    getQuoteRequestStatusForJobItem(jobItem: JobItem) {
        return this.getQuoteRequestStatusForItem(jobItem, this.job().QuoteRequests);
    }

    getQuoteRequestStatusForEstimateItem(estimateItem: EstimateItem) {
        return this.getQuoteRequestStatusForItem(estimateItem, this.job().Estimate.QuoteRequests);
    }

    getQuoteRequestStatusForItem(item: any, collection?: BreezeCase<QuoteRequest>[]) {
        if (!item || !item.ID_QuoteRequest) return null;
        const qr = (collection ?? []).find((qr) => {
            return qr.ID_QuoteRequest === item.ID_QuoteRequest;
        });
        if (!qr) return null;
        return qr.Status;
    }

    private setSelectedRFQForItems() {
        this.jobItems.forEach((category: JobItem) => {
            if (!category?.children || !category.children?.length) return;
            category.children.forEach((item: JobItem) => {
                if (item.ID_QuoteRequestItem && this.job().QuoteRequests?.length) {
                    const quoteRequest = this.job().QuoteRequests!.find((qr) => item.ID_QuoteRequest === qr.ID_QuoteRequest);

                    if (quoteRequest) {
                        item.hasRelatedQuoteRequest = quoteRequest.QuoteRequestRecipients?.some((qrr) => qrr.IsSelectedQuote);
                    }
                }
            });
        });
    }

    setCostItem(jobItem: JobItem, match: any) {
        const costItem = match.item || match;
        const id = costItem.ID_CostItem || costItem.costItemId;
        // set properties from selection
        jobItem.ID_CostItem = id;
        jobItem.Description = costItem.description;
        jobItem.UOM = costItem.uom;
        jobItem.EstimatedUnitCost = costItem.unitCost;
        jobItem.CostItemType = this.taxService.validateCostItemType(costItem.costItemType);
        jobItem.ItemCode = costItem.itemCode;

        // Get latest version of item from server
        this.#cataloguesService.GetCostItem(id).then((data) => {
            // @ts-ignore TS2531
            if (data.results.length > 0) {
                // @ts-ignore TS2531
                const item = data.results[0];

                jobItem.ID_CostItem = item.ID_CostItem;
                jobItem.Description = item.Description;
                jobItem.UOM = item.UOM;
                jobItem.EstimatedUnitCost = item.UnitCost;
                jobItem.CostItemType = this.taxService.validateCostItemType(item.CostItemType);
                jobItem.ItemCode = item.ItemCode;
            }
        });
    }

    saveBeforeRouting(to: string[], queryParams: Params) {
        this.#dialogService
            .show({
                type: 'warning',
                title: 'Unsaved Changes',
                text: 'You have not finished editing a form. Please save changes before leaving this page.',
                okText: 'Save',
                showCancel: true,
                cancelText: 'Discard changes',
                showCloseButton: true,
            })
            .then(
                async () => {
                    if (this.itemsForm) await this.stopEditing(this.itemsForm);
                    if (!this.saveError) this.#router.navigate(to, { queryParams });
                },
                (dismiss) => {
                    if (dismiss === 'close') return;
                    this.cancelEditing();
                    this.#router.navigate(to, { queryParams });
                }
            );
    }
}

interface ActualCostingsQuoteRequestSlim {
    Title: string;
    ID_QuoteRequest: string;
}
